import {
	Checkmark,
	CloseThin,
	XIcon,
	LeftArrow,
	Chevron,
	ExclamationMarkRed
} from '@/assets/images';
import Loader, { Status } from '@/components/Loader/Loader';
import BusinessInfoTab from '@/modules/dashboard/components/BusinessInfoTab/BusinessInfoTab';
import type {
	Business,
	ContactType,
	UserNotes
} from '@/modules/dashboard/dashboardTypes';
import type { RootState } from '@/rootReducer';
import * as dayjs from 'dayjs';

import 'moment-timezone';
import { useLocation } from 'react-router';
import React, { HTMLAttributes, useEffect, useState, useRef } from 'react';
import toast from 'react-hot-toast';

import { shallowEqual, useSelector, useDispatch } from 'react-redux';
import { sendAnalytics } from '@/services/analyticsService';
import {
	emailSendCustomerReportBadData,
	emailSendCustomerReportBadDataClear
} from '@/modules/email/emailActions';
import type { DirectEmail } from '@/modules/email/emailTypes';
import BusinessEmailsTab from '../BusinessEmailsTab/BusinessEmailsTab';
import BusinessActivityTab from '../BusinessActivityTab/BusinessActivityTab';
import {
	dashboardAddBusinessData,
	dashboardDeleteBusinessData,
	dashboardAddBusinessDataReset,
	dashboardSetBusinessReminder,
	dashboardDeleteBusinessReminder,
	dashboardResetBusinessReminder,
	dashboardCloseBusinessPreview,
	dashboardSelectBusinessTab
} from '../../dashboardActions';
import './BusinessPreview.scss';

interface Props extends HTMLAttributes<HTMLDivElement> {
	business: Partial<Business>;
	saveNote?: (businessId: string, notes: UserNotes) => void;
}

interface PreviewProps extends Props {
	business: Business;
	saveBusinessToList?: (b: Business[]) => void;
	getEmailThread: (b: Business) => void;
	onEmail: (b: DirectEmail) => void;
	parentNodeName?: string;
	setAsOpen?: boolean;
	toggleBusinessPreview?: () => void;
	setEmailAsOpen?: boolean;
	openDeleteNoteModal: () => void;
	deleteDataIndex: number | null;
	setDeleteDataIndex: (x: number) => void;
	goToMap?: (businessId: string) => void;
	initialTab?: string; // Add initialTab prop
}

const BusinessPreview = ({
	business,
	saveNote,
	saveBusinessToList,
	getEmailThread,
	parentNodeName,
	onEmail,
	setAsOpen,
	toggleBusinessPreview,
	setEmailAsOpen,
	openDeleteNoteModal,
	deleteDataIndex,
	setDeleteDataIndex,
	goToMap
}: PreviewProps): JSX.Element => {
	const [
		notesIsLoading,
		notesFailure,
		taskLoading,
		taskFailure,
		taskSuccess,
		deleteSuccess,
		addBusinessDataLoading,
		addBusinessDataSuccess,
		addBusinessDataFailure,
		selectedList,
		selectedLocation,
		user,
		closeBusinessPreview,
		businesses,
		openBusinessToNote,
		reportBadDataLoading,
		reportBadDataFailure,
		reportBadDataSuccess,
		selectedTabFromRedux
	] = useSelector(
		(state: RootState) => [
			state.dashboard.getIn(['notes', 'isLoading']),
			state.dashboard.getIn(['notes', 'failure']),
			state.dashboard.getIn(['taskModal', 'isLoading']),
			state.dashboard.getIn(['taskModal', 'failure']),
			state.dashboard.getIn(['taskModal', 'success']),
			state.dashboard.getIn(['taskModal', 'delete', 'success']),
			state.dashboard.getIn(['businesses', 'addedDataLoading']),
			state.dashboard.getIn(['businesses', 'addedDataSuccess']),
			state.dashboard.getIn(['businesses', 'addedDataFailure']),
			state.dashboard.getIn(['lists', 'selected']).toJS(),
			state.dashboard.getIn(['locations', 'selected']).toJS(),
			state.user.getIn(['authorize', 'user']).toJS(),
			state.dashboard.getIn(['businesses', 'closePreview']),
			state.dashboard.getIn(['businesses', 'data']).toJS(),
			state.dashboard.getIn(['businesses', 'openToNote']),
			state.email.getIn(['reportBadData', 'loading']),
			state.email.getIn(['reportBadData', 'failure']),
			state.email.getIn(['reportBadData', 'success']),
			state.dashboard.getIn(['businesses', 'selectedTab'])
		],
		shallowEqual
	);

	const [status, setStatus] = useState<Status>(Status.NONE);
	const [businessBureauDropDown, setBusinessBureauDropDown] = useState(false);
	const [userGenDataType, setUserGenDataType] = useState('email');
	const [modalClickBlocker, setModalClickBlocker] = useState(false);
	const [emailHistoryOpen, setEmailHistoryOpen] = useState(false);
	const [showComposeEmail, setShowComposeEmail] = useState<boolean>(false);
	const [showPreview, setShowPreview] = useState<boolean>(false);
	const [showEmailReplyButton, setShowEmailReplyButton] =
		useState<boolean>(true);
	const [composeEmailMessage, setComposeEmailMessage] = useState<string>('');
	const [reportDataDropdownOpened, setReportDataDropdownOpened] =
		useState<boolean>(false);
	const [reportDataSelected, setReportDataSelected] = useState<string>('');
	const [reportDataModalInputText, setReportDataModalInputText] =
		useState<string>('');

	const businessPreviewHolderRef = useRef<HTMLDivElement>(null);
	const reportDataDropdownRef = useRef<HTMLDivElement>(null);

	const dispatch = useDispatch();

	const addBusinessData = (data: any) =>
		dispatch(dashboardAddBusinessData(data));
	const deleteBusinessData = (data: any) =>
		dispatch(dashboardDeleteBusinessData(data));
	const resetBusinessData = () => dispatch(dashboardAddBusinessDataReset());
	const closeBusinessPreviewFunc = (boolean: boolean) =>
		dispatch(dashboardCloseBusinessPreview(boolean));
	const sendCustomerReportBadData = (
		businessId: string,
		incorrectData: string,
		userId: string,
		additionalString?: string
	) =>
		dispatch(
			emailSendCustomerReportBadData({
				businessId,
				incorrectData,
				userId,
				additionalString
			})
		);
	const sendCustomerReportBadDataClear = () =>
		dispatch(emailSendCustomerReportBadDataClear());

	const { pathname } = useLocation();

	const handleOpenComposeEmail = (emailBusiness: Business, email: string) => {
		onEmail({
			business: emailBusiness,
			email
		});

		sendAnalytics({
			type: 'track',
			userId: user.email,
			eventName: `User opened a direct email${pathname === '/communication-center' ? ' - Communication Center page' : ''}`,
			properties: {
				businessName: emailBusiness.name,
				businessId: emailBusiness._id || emailBusiness.id,
				businessAddress: emailBusiness.address
			}
		});
	};

	useEffect(() => {
		if (
			!selectedList?.businessIds?.find(
				(id: string) => id === business?._id
			)
		) {
			setBusinessPreviewOpen(false);
		}
	}, [selectedList?.businessIds?.length]);

	useEffect(() => {
		if (
			!selectedList?.businessIds?.find(
				(id: string) => id === business?._id
			)
		) {
			setBusinessPreviewOpen(false);
		}
	}, [selectedLocation?.id]);

	useEffect(() => {
		if (status !== Status.NONE) {
			setStatus(Status.NONE);
		}
	}, [JSON.stringify(business)]);

	const setReminderDispatch = payload =>
		dispatch(dashboardSetBusinessReminder(payload));
	const deleteReminderDispatch = payload =>
		dispatch(dashboardDeleteBusinessReminder(payload));
	const resetReminderDispatch = () =>
		dispatch(dashboardResetBusinessReminder());

	useEffect(() => {
		if (notesIsLoading) {
			setStatus(Status.LOADING);
		} else if (status === Status.LOADING) {
			if (String(notesFailure).length === 0) {
				setTimeout(() => {
					setStatus(Status.SUCCESS);
				}, 500);
			} else {
				setTimeout(() => setStatus(Status.FAILURE), 500);
			}
		}
	}, [notesFailure, notesIsLoading, status, businessBureauDropDown]);

	const primaryEmail = business?.facebook?.email
		? String(business?.facebook?.email).toLowerCase()
		: '';

	const secondaryEmail =
		business?.email && business.email !== primaryEmail
			? String(business?.email).toLowerCase()
			: '';
	const thirdEmail =
		business?.web?.email &&
		business.web.email !== primaryEmail &&
		business.web.email !== secondaryEmail
			? String(business?.web?.email).toLowerCase()
			: '';

	let facebookUrl: URL | undefined;

	if (business.facebook?.url) {
		facebookUrl = new window.URL(business.facebook?.url);
	}

	const toggleContacted = (type: string, link = false) => {
		let contactType: ContactType;

		if (business.contactType && link) {
			contactType = {
				...business.contactType,
				...business.lead,
				[type]: true
			};
		} else if (business.contactType) {
			contactType = {
				...business.contactType,
				...business.lead,
				[type]: !(
					business.contactType[type as keyof ContactType] ?? false
				)
			};
		} else {
			contactType = {
				[type]: true
			};
		}

		let businessIsContacted: boolean | undefined;
		let userAddedContact = false;
		if (business.userAddedData) {
			business.userAddedData.forEach(data => {
				if (data.checked) {
					userAddedContact = true;
				}
			});
		}

		if (
			[
				'messenger',
				'phone',
				'emailPrimary',
				'emailSecondary',
				'thirdEmail',
				'instagramMessenger'
			].includes(type)
		) {
			businessIsContacted =
				contactType?.messenger ||
				contactType?.phone ||
				contactType?.emailPrimary ||
				contactType?.emailSecondary ||
				contactType?.thirdEmail ||
				contactType?.instagramMessenger ||
				userAddedContact;
		} else {
			businessIsContacted = business.isContacted;
		}

		if (saveNote) {
			const businessId =
				business._id || business.businessId || business.id;
			const currentNoteEl = document.getElementsByClassName(
				'business-notes--text'
			)[0];
			let currentNoteVal;
			if (currentNoteEl) {
				currentNoteVal = currentNoteEl.value;
			}
			saveNote(businessId as string, business.name, {
				isContacted: businessIsContacted,
				lead: business?.lead,
				note: business?.note ?? '',
				currentNoteText: currentNoteVal,
				contactType,
				userAddedData: business.userAddedData
			} as UserNotes);
			if (user && user.email) {
				sendAnalytics({
					type: 'track',
					userId: user.email,
					eventName: `User contacted a business via their: ${type.toUpperCase()}${pathname === '/communication-center' ? ' - Communication Center page' : ''}`,
					properties: {
						businessName: business.name,
						businessId: business._id || business.id,
						businessAddress: business.address
					}
				});
			}
		}
	};

	const toggleContactedUserData = index => {
		business.userAddedData[index].checked =
			!business.userAddedData[index].checked;
		let contacted = false;
		if (business.userAddedData) {
			business.userAddedData.forEach(data => {
				if (data.checked) {
					contacted = true;
				}
			});
		}
		if (business.contactType) {
			const contactKeys = Object.keys(business.contactType);
			if (contactKeys.length > 0) {
				contactKeys.forEach(key => {
					if (business.contactType[key]) {
						contacted = true;
					}
				});
			}
		}
		const note = {
			isContacted: contacted,
			lead: business.lead,
			note: business.note ?? '',
			contactType: business.contactType,
			userAddedData: business.userAddedData ?? ''
		};

		const businessId = business._id || business.businessId;

		saveNote(businessId as string, business.name, note as UserNotes);
		if (user && user.email) {
			sendAnalytics({
				type: 'track',
				userId: user.email,
				eventName: `User contacted a business via their User Added Data${pathname === '/communication-center' ? ' - Communication Center page' : ''}`,
				properties: {
					address: business?.address,
					id: business?._id,
					name: business?.name
				}
			});
		}
	};

	const toggleLead = (value: boolean | undefined | null) => {
		if (saveNote) {
			const businessId = business._id || business.businessId;
			const currentNoteEl = document.getElementsByClassName(
				'business-notes--text'
			)[0];
			let currentNoteVal;
			if (currentNoteEl) {
				currentNoteVal = currentNoteEl.value;
			}
			saveNote(businessId as string, business.name, {
				isContacted: business?.isContacted,
				lead: value,
				note: business?.note ?? '',
				currentNoteText: currentNoteVal,
				contactType: business?.contactType,
				userAddedData: business.userAddedData ?? ''
			} as UserNotes);
			if (user && user.email) {
				sendAnalytics({
					type: 'track',
					userId: user.email,
					eventName: 'Lead was changed from the business preview',
					properties: {
						address: business?.address,
						id: business?._id,
						name: business?.name
					}
				});
			}
			if (value === undefined) {
				if (user && user.email) {
					sendAnalytics({
						type: 'track',
						userId: user.email,
						eventName: `Lead was set to neutral${pathname === '/communication-center' ? ' - Communication Center page' : ''}`,
						properties: {
							address: business?.address,
							id: business?._id,
							name: business?.name
						}
					});
				}
			}
			if (value === true) {
				if (user && user.email) {
					sendAnalytics({
						type: 'track',
						userId: user.email,
						eventName: `Lead was added${pathname === '/communication-center' ? ' - Communication Center page' : ''}`,
						properties: {
							address: business?.address,
							id: business?._id,
							name: business?.name
						}
					});
				}
			}
			if (value === false) {
				if (user && user.email) {
					sendAnalytics({
						type: 'track',
						userId: user.email,
						eventName: `Lead was removed${pathname === '/communication-center' ? ' - Communication Center page' : ''}`,
						properties: {
							address: business?.address,
							id: business?._id,
							name: business?.name
						}
					});
				}
			}
		}
	};

	const openSetReminder = () => {
		const modalEl = document.getElementsByClassName(
			`reminders-modal-${parentNodeName}`
		)[0];
		if (!business.reminder?.hasReminderTaskSet) {
			const notesEl = modalEl.querySelectorAll('.text-note');
			if (notesEl) {
				for (let i = 0; i < notesEl.length; i++) {
					notesEl[i].value = '';
				}
			}
		}
		const zChangeEl = modalEl.parentNode.parentNode.querySelector(
			'.dashboard-container--resizable-wrapper'
		);
		if (zChangeEl) {
			zChangeEl.style.zIndex = 1;
		}
		if (modalEl) {
			modalEl.classList.add('modal-exists');
			setTimeout(() => {
				modalEl.classList.add('modal-visible');
			});
		}
	};

	const closeSetReminder = () => {
		const modalEl = document.getElementsByClassName(
			`reminders-modal-${parentNodeName}`
		)[0];
		if (modalEl) {
			modalEl.classList.remove('modal-visible');
			const zChangeEl = modalEl.parentNode.parentNode.querySelector(
				'.dashboard-container--resizable-wrapper'
			);
			if (zChangeEl) {
				zChangeEl.style.zIndex = 150;
			}
			const optionEl = document.getElementsByClassName(
				'extra-hour-selection'
			)[0];
			if (optionEl) {
				optionEl.remove();
			}
			setTimeout(() => {
				if (modalEl) {
					const monthEl = modalEl.querySelector('#month-picker');
					if (monthEl) {
						monthEl.value = 0;
					}
					const dayEl = modalEl.querySelector('#day-picker');
					if (dayEl) {
						dayEl.value = 0;
					}
					const hourEl = modalEl.querySelector('#hour-picker');
					if (hourEl) {
						hourEl.value = 1;
					}
					modalEl.classList.remove('modal-exists');
				}
				resetReminderDispatch();
			}, 300);
		}
	};

	const setReminder = async () => {
		if (taskLoading) {
			return;
		}
		const modalEl = document.getElementsByClassName(
			`reminders-modal-${parentNodeName}`
		)[0];
		const note = modalEl.querySelector('.text-note').value;
		const monthVal =
			modalEl.querySelector('#month-picker').value * 30 * 24 * 60 * 60;
		const dayVal =
			modalEl.querySelector('#day-picker').value * 24 * 60 * 60;
		const hourVal = modalEl.querySelector('#hour-picker').value * 60 * 60;

		const totalTimeInSeconds = monthVal + dayVal + hourVal;
		const triggerDate = totalTimeInSeconds + Date.now() / 1000;
		sendAnalytics({
			type: 'track',
			userId: user.email,
			eventName: `User set a reminder${pathname === '/communication-center' ? ' - Communication Center page' : ''}`,
			properties: {
				businessId: business.businessId || business._id,
				businessName: business.name,
				text: note
			}
		});
		const dataObj = {
			business,
			note,
			triggerDate,
			noteHtml: note.replace(/\r\n|\r|\n/g, '</br>'),
			originalNote: business.note ? business.note : '',
			businessId: business._id || business.businessId,
			businessName: business.name,
			userEmail: user.email
		};

		if (selectedList && Object.keys(selectedList).length > 0) {
			dataObj.listId = selectedList.id;
		}

		await setReminderDispatch(dataObj);
	};

	const deletReminder = async () => {
		sendAnalytics({
			type: 'track',
			userId: user.email,
			eventName: `User deleted a reminder${pathname === '/communication-center' ? ' - Communication Center page' : ''}`,
			properties: {
				businessId: business.businessId || business._id,
				businessName: business.name
			}
		});
		const dataObj = {
			businessId: business._id || business.businessId,
			businessName: business.name,
			userEmail: user.email
		};

		if (selectedList && Object.keys(selectedList).length > 0) {
			dataObj.listId = selectedList.id;
		}

		await deleteReminderDispatch(dataObj);
	};

	const taskCloseSetReminder = () => {
		if (taskSuccess === true) {
			closeSetReminder();
		}
	};

	const deleteCloseSetReminder = () => {
		if (deleteSuccess === true) {
			closeSetReminder();
		}
	};

	useEffect(() => {
		taskCloseSetReminder();
	}, [taskSuccess]);

	useEffect(() => {
		deleteCloseSetReminder();
	}, [deleteSuccess]);

	const setHourZero = () => {
		const modalEl = document.getElementsByClassName(
			`reminders-modal-${parentNodeName}`
		)[0];
		const monthVal = modalEl.querySelector('#month-picker').value;
		const dayVal = modalEl.querySelector('#day-picker').value;
		const hourVal = modalEl.querySelector('#hour-picker').value;
		const selectEl = modalEl.querySelector('#hour-picker');
		if (monthVal > 0 || dayVal > 0) {
			const node = document.createElement('option');
			node.classList.add('extra-hour-selection');
			node.value = 0;
			const textNode = document.createTextNode('0 hours');
			node.appendChild(textNode);
			selectEl.prepend(node);
			if (hourVal === 1 || hourVal === '1') {
				setTimeout(() => {
					modalEl.querySelector('#hour-picker').value = 0;
				}, 100);
			}
		} else {
			if (hourVal === 0) {
				modalEl.querySelector('#hour-picker').value = 1;
			}
			const optionEl = document.getElementsByClassName(
				'extra-hour-selection'
			)[0];
			if (optionEl) {
				optionEl.remove();
			}
		}
	};

	const months = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
	const days = [
		0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19,
		20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30
	];
	const hours = [
		1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20,
		21, 22, 23, 24
	];

	const clearInputs = () => {
		const modalEl = document.getElementsByClassName('add-data-modal')[0];
		const mainDataEl = modalEl.querySelector('.top-input');
		if (mainDataEl) {
			mainDataEl.value = '';
		}
		const linkDataEl = modalEl.querySelector('.bottom-input');
		if (mainDataEl) {
			linkDataEl.value = '';
		}
		modalEl.classList.remove('email-error');
	};

	const removeEmailError = () => {
		const modalEl = document.getElementsByClassName('add-data-modal')[0];
		modalEl.classList.remove('email-error');
		modalEl.classList.remove('email-exists-error');
	};

	const removeLinkError = () => {
		const modalEl = document.getElementsByClassName('add-data-modal')[0];
		modalEl.classList.remove('link-error');
		modalEl.classList.remove('link-exists-error');
		modalEl.classList.remove('linkName-exists-error');
	};

	const removePhoneError = () => {
		const modalEl = document.getElementsByClassName('add-data-modal')[0];
		modalEl.classList.remove('phone-exists-error');
	};

	const removeEmptyInputError = () => {
		const modalEl = document.getElementsByClassName('add-data-modal')[0];
		modalEl.classList.remove('empty-input-error');
	};

	const removeTypeError = () => {
		const modalEl = document.getElementsByClassName('add-data-modal')[0];
		modalEl.classList.remove('type-error');
	};

	const removeAllErrors = () => {
		removeTypeError();
		removeEmptyInputError();
		removePhoneError();
		removeLinkError();
		removeEmailError();
	};

	const setAsDataType = (evt: any) => {
		if (userGenDataType !== evt.target.id) {
			clearInputs();
			setUserGenDataType(evt.target.id);
			removeAllErrors();
		}
	};

	const openAddDataModal = () => {
		const modalEl = document.getElementsByClassName('add-data-modal')[0];
		modalEl.classList.add('modal-exists');
		const zChangeEl = modalEl.parentNode.parentNode.querySelector(
			'.dashboard-container--resizable-wrapper'
		);
		if (zChangeEl) {
			zChangeEl.style.zIndex = 1;
		}
		setTimeout(() => {
			modalEl.classList.add('modal-visible');
		});
	};

	const closeAddDataModal = () => {
		const modalEl = document.getElementsByClassName('add-data-modal')[0];
		modalEl.classList.remove('modal-visible');
		const zChangeEl = modalEl.parentNode.parentNode.querySelector(
			'.dashboard-container--resizable-wrapper'
		);
		if (zChangeEl) {
			zChangeEl.style.zIndex = 150;
		}
		setTimeout(() => {
			setUserGenDataType('email');
			removeAllErrors();
			clearInputs();
			modalEl.classList.remove('modal-exists');
		}, 300);
	};

	useEffect(() => {
		if (addBusinessDataSuccess === true) {
			closeAddDataModal();
			resetBusinessData();
		}
	}, [addBusinessDataSuccess]);

	const setUserGeneratedData = () => {
		const cleanUpData = value => {
			return value
				?.trim()
				.replace(/[\(\)-.\s]/gim, '')
				.replace('www', '')
				.replace('http://', '')
				.replace('https://', '')
				.trim();
		};
		const doesUserAddedDataExist =
			business.userAddedData && business.userAddedData.length > 0;
		if (modalClickBlocker) {
			return;
		}
		setModalClickBlocker(true);
		setTimeout(() => {
			setModalClickBlocker(false);
		}, 2500);
		const modalEl = document.getElementsByClassName('add-data-modal')[0];
		if (!userGenDataType) {
			modalEl.classList.add('type-error');
			return;
		}
		const rawMainData = modalEl
			.querySelector('.top-input')
			.value.toLowerCase();
		const mainData = cleanUpData(rawMainData);
		const existsUserAddedData =
			business.userAddedData?.filter(b => {
				return cleanUpData(b.data) === mainData;
			}).length > 0;
		let linkData;
		let rawLinkData;
		removeAllErrors();
		if (!mainData || mainData === '') {
			return modalEl.classList.add('empty-input-error');
		}
		if (userGenDataType === 'web-link') {
			const urlReg =
				/^((https?|ftp|smtp):\/\/)?(www.)?[a-z0-9]+(\.[a-z]{2,}){1,3}(#?\/?[a-zA-Z0-9#]+)*\/?(\?[a-zA-Z0-9-_]+=[a-zA-Z0-9-%]+&?)?$/;
			rawLinkData = modalEl
				.querySelector('.bottom-input')
				.value.toLowerCase();
			linkData = cleanUpData(rawLinkData);
			if (!linkData || linkData === '') {
				return modalEl.classList.add('empty-input-error');
			}
			if (!urlReg.test(rawLinkData)) {
				return modalEl.classList.add('link-error');
			}

			if (doesUserAddedDataExist) {
				const doesSanitizedDataExistsInUserAddedData =
					business.userAddedData?.filter(b => {
						if (b.linkData) {
							const sanitizedLinkData = cleanUpData(linkData);
							const sanitizedBLinkData = cleanUpData(b.linkData);
							return sanitizedBLinkData === sanitizedLinkData;
						}
					}).length > 0;

				if (doesSanitizedDataExistsInUserAddedData) {
					return modalEl.classList.add('link-exists-error');
				}

				if (existsUserAddedData) {
					return modalEl.classList.add('linkName-exists-error');
				}
			}

			modalEl.classList.remove('link-error');
		}
		if (userGenDataType === 'email') {
			const emailRegExp =
				/^(([^<>()[\].,;:\s@"]+(\.[^<>()[\].,;:\s@"]+)*)|(".+"))@(([^<>()[\].,;:\s@"]+\.)+[^<>()[\].,;:\s@"]{2,})$/;
			const emailValid = emailRegExp.test(rawMainData);
			const emailExists = (() => {
				if (
					business.email &&
					mainData === cleanUpData(business.email)
				) {
					return true;
				}
				if (
					business.web &&
					business.web.email &&
					mainData === cleanUpData(business.web.email)
				) {
					return true;
				}
				if (
					business.facebook &&
					business.facebook.email &&
					mainData === cleanUpData(business.facebook.email)
				) {
					return true;
				}

				if (doesUserAddedDataExist && existsUserAddedData) {
					return true;
				}
				return false;
			})();
			if (!emailValid) {
				return modalEl.classList.add('email-error');
			}
			if (emailExists) {
				return modalEl.classList.add('email-exists-error');
			}
			modalEl.classList.remove('email-error');
		}

		if (userGenDataType === 'phone') {
			if (mainData === cleanUpData(business.phone.phone)) {
				return modalEl.classList.add('phone-exists-error');
			}

			if (doesUserAddedDataExist && existsUserAddedData) {
				return modalEl.classList.add('phone-exists-error');
			}
		}

		const businessId = business._id || business.businessId || business.id;
		const dataObj = {
			type: userGenDataType,
			businessId,
			userEmail: user.email,
			mainData: rawMainData,
			linkData: rawLinkData
		};

		if (selectedList && Object.keys(selectedList).length > 0) {
			dataObj.listId = selectedList.id;
		}

		addBusinessData(dataObj);
	};

	const deleteUserAddedData = (dataInd, businessId, userEmail) => {
		const dataObj = {
			businessId,
			dataInd,
			userEmail
		};

		if (selectedList && Object.keys(selectedList).length > 0) {
			dataObj.listId = selectedList.id;
		}

		deleteBusinessData(dataObj);
	};

	const closeEmailHistory = () => {
		setEmailHistoryOpen(false);
	};

	useEffect(() => {
		closeEmailHistory();
	}, [business._id]);

	const handleInputKeyDown = (
		e: React.KeyboardEvent<HTMLInputElement>
	): void => {
		if (e.key === 'Tab') e.preventDefault();
	};

	const selectTab = tabType => {
		setCurrentTab(tabType);
		
		// Dispatch action to update the Redux state with the selected tab
		dispatch(dashboardSelectBusinessTab(tabType));
		
		if (
			user &&
			user.email &&
			(tabType === 'emails' || tabType === 'activity')
		) {
			sendAnalytics({
				type: 'track',
				userId: user.email,
				eventName: `Opened the ${tabType} tab`,
				properties: {
					businessId: business.businessId || business._id,
					businessName: business.name
				}
			});
		}
	};

	const [currentTab, setCurrentTab] = useState(selectedTabFromRedux);

	const [businessPreviewOpen, setBusinessPreviewOpen] = useState(false);

	useEffect(() => {
		if (business && business.name) {
			setBusinessPreviewOpen(true);
			if (openBusinessToNote) {
				selectTab('activity');
			} else if (setEmailAsOpen || selectedTabFromRedux === 'emails') {
				// If the email tab was selected previously, maintain it
				selectTab('emails');
				getEmailThread(business);
			} else {
				selectTab(selectedTabFromRedux || 'info');
			}
		}
	}, [business.name, selectedTabFromRedux]);

	useEffect(() => {
		if (closeBusinessPreview && businessPreviewOpen) {
			setBusinessPreviewOpen(false);
			closeBusinessPreviewFunc(false);
		} else if (closeBusinessPreview && !businessPreviewOpen) {
			setBusinessPreviewOpen(true);
			// selectTab('info');
			closeBusinessPreviewFunc(false);
		}
	}, [closeBusinessPreview]);

	useEffect(() => {
		if (businessPreviewOpen) {
			setBusinessPreviewOpen(false);
			closeBusinessPreviewFunc(false);
		}
	}, [businesses[0]?._id]);

	const openDeleteDataModal = (index: number) => {
		const e = document.getElementById('delete-data-modal');
		e?.classList.remove('delete-data-modal-fade');
		setTimeout(() => {
			e?.classList.remove('delete-data-modal-closed');
		}, 500);

		setDeleteDataIndex(index);
	};

	const closeModal = (className: string) => {
		const e = document.getElementById(className);
		e?.classList.add(`${className}-fade`);
		setTimeout(() => {
			e?.classList.add(`${className}-closed`);
		}, 500);
	};

	const handleOpenReportIncorrectDataModal = (className: string) => {
		const e = document.getElementById(className);
		e.classList.remove(`${className}-closed`);
		setTimeout(() => e.classList.remove(`${className}-fade`), 0);
	};

	const closeReportDataModal = () => {
		closeModal('report-data-modal');
		setReportDataSelected('');
		setReportDataModalInputText('');
		sendCustomerReportBadDataClear();
	};

	const closeEmailUnavailableModal = () => {
		const e = document.getElementById('email-unavailable-modal');
		e.classList.add('email-unavailable-modal-fade');
		setTimeout(() => {
			e.classList.add('email-unavailable-modal-closed');
		}, 500);
	};

	const onClickDelete = () => {
		setComposeEmailMessage('');
		setShowComposeEmail(false);
		setShowPreview(false);
		setShowEmailReplyButton(true);
		closeModal('delete-modal');
	};

	useEffect(() => {
		if (currentTab !== 'emails') {
			setShowComposeEmail(false);
		}
	}, [currentTab]);

	useEffect(() => {
		if (
			businessPreviewHolderRef.current?.scrollTop !== 0 &&
			currentTab !== 'activity'
		) {
			businessPreviewHolderRef.current?.scroll(0, 0);
		}
	}, [currentTab, business]);

	const closeReportDataDropdown = e => {
		if (!reportDataDropdownRef?.current?.contains(e.target)) {
			setReportDataDropdownOpened(false);
		}
	};

	useEffect(() => {
		window.addEventListener('click', closeReportDataDropdown);

		return () =>
			window.removeEventListener('click', closeReportDataDropdown);
	}, []);

	useEffect(() => {
		if (reportBadDataSuccess) {
			closeModal('report-data-modal');
			setReportDataSelected('');
			setReportDataModalInputText('');
			toast.success(
				'Data submitted! We are grateful for your efforts in enhancing the accuracy of data on Resquared. Your commitment is greatly valued by our team.',
				{
					duration: 5000
				}
			);

			setTimeout(() => {
				sendCustomerReportBadDataClear();
			}, 5000);
		}
	}, [reportBadDataSuccess]);

	return (
		<>
			<div
				id="business-preview-container"
				className={`business-preview-holder ${businessPreviewOpen ? 'business-preview-open' : ''} ${setAsOpen ? 'business-preview-open' : ''} `}
				ref={businessPreviewHolderRef}
			>
				<div className="business-preview-header">
					{selectedList && (
						<div className="business-preview-list-name">
							{selectedList.name}
						</div>
					)}
					<div className="business-preview-inner-div">
						<div className="business-preview-business-name">
							<span
								className="business-preview-close-button-mobile"
								onClick={() => {
									setBusinessPreviewOpen(false);
									if (toggleBusinessPreview) {
										toggleBusinessPreview();
									}
								}}
							>
								<LeftArrow className="close-button-arrow" />
							</span>
							{business.name}
						</div>
						{pathname !== '/communication-center' &&
							!user?.features?.platformRedesign && (
								<div
									className="business-preview-add-to-list-button"
									onClick={() => {
										saveBusinessToList([business]);
									}}
								>
									<CloseThin className="list-button-cross" />{' '}
									<span>Add to list</span>
								</div>
							)}
						<span className="empty-space" />
					</div>
					<div
						className="business-preview-close-button"
						onClick={() => {
							setBusinessPreviewOpen(false);
							if (toggleBusinessPreview) {
								toggleBusinessPreview();
							}
						}}
					>
						<XIcon className="close-button-cross" />
					</div>
					<div className="business-preview-tabs">
						<div
							className={`business-preview-tab ${currentTab === 'info' ? 'business-preview-active-tab' : ''} ${
								(!user.threadedEmail ||
									Object.keys(user.threadedEmail).length ===
										0 ||
									(selectedLocation?.id &&
										pathname !==
											'/communication-center')) &&
								(!user.re2HostedEmail ||
									Object.keys(user.re2HostedEmail).length ===
										0 ||
									(selectedLocation?.id &&
										pathname !==
											'/communication-center')) &&
								(!user.re2SharedDomain ||
									user?.features?.sharedDomainVersion ==
										'v2' ||
									(selectedLocation?.id &&
										pathname !== '/communication-center'))
									? 'double-tab'
									: ''
							} `}
							onClick={() => {
								selectTab('info');
							}}
						>
							INFO
						</div>
						{((selectedList &&
							Object.keys(selectedList).length > 0) ||
							pathname === '/communication-center') &&
						user &&
						(Object.keys(user.threadedEmail).length > 0 ||
							user.re2HostedEmail ||
							(user.re2SharedDomain &&
								user?.features?.sharedDomainVersion !==
									'v2')) ? (
							<div
								className={`business-preview-tab ${currentTab === 'emails' ? 'business-preview-active-tab' : ''}`}
								onClick={() => {
									selectTab('emails');
								}}
							>
								EMAILS
							</div>
						) : (
							''
						)}
						<div
							id="business-preview-activity-tab"
							className={`business-preview-tab ${currentTab === 'activity' ? 'business-preview-active-tab' : ''} ${
								(!user.threadedEmail ||
									Object.keys(user.threadedEmail).length ===
										0 ||
									(selectedLocation?.id &&
										pathname !==
											'/communication-center')) &&
								(!user.re2HostedEmail ||
									Object.keys(user.re2HostedEmail).length ===
										0 ||
									(selectedLocation?.id &&
										pathname !==
											'/communication-center')) &&
								(!user.re2SharedDomain ||
									user?.features?.sharedDomainVersion ==
										'v2' ||
									(selectedLocation?.id &&
										pathname !== '/communication-center'))
									? 'double-tab'
									: ''
							} `}
							onClick={() => {
								selectTab('activity');
							}}
						>
							ACTIVITY
						</div>
					</div>
				</div>
				{currentTab === 'info' && (
					<div className="business-preview-body-holder">
						<BusinessInfoTab
							selectBusiness={business}
							openSetReminder={openSetReminder}
							openAddDataModal={openAddDataModal}
							toggleContactedUserData={toggleContactedUserData}
							toggleContacted={toggleContacted}
							openComposeEmail={handleOpenComposeEmail}
							selectedList={selectedList}
							toggleLead={toggleLead}
							parentNodeName={parentNodeName}
							saveNote={saveNote}
							openDeleteDataModal={openDeleteDataModal}
							openReportIncorrectDataModal={
								handleOpenReportIncorrectDataModal
							}
							goToMap={goToMap}
						/>
					</div>
				)}
				{currentTab === 'activity' && (
					<div className="business-preview-body-holder">
						<BusinessActivityTab
							openDeleteNoteModal={openDeleteNoteModal}
							openOnEmail={handleOpenComposeEmail}
							selectTab={selectTab}
						/>
					</div>
				)}
				{currentTab === 'emails' && (
					<div className="business-preview-body-holder">
						<BusinessEmailsTab
							business={business}
							openReportIncorrectDataModal={
								handleOpenReportIncorrectDataModal
							}
							showComposeEmail={showComposeEmail}
							setShowComposeEmail={setShowComposeEmail}
							showPreview={showPreview}
							setShowPreview={setShowPreview}
							message={composeEmailMessage}
							setMessage={setComposeEmailMessage}
							showEmailReplyButton={showEmailReplyButton}
							setShowEmailReplyButton={setShowEmailReplyButton}
							getEmailThread={getEmailThread}
							openComposeEmail={handleOpenComposeEmail}
							selectTab={selectTab}
						/>
					</div>
				)}
			</div>
			<div
				className={`reminders-modal reminders-modal-${parentNodeName}`}
			>
				<div className="reminders-modal-holder-inner">
					<div
						aria-label="Close Reminders modal."
						role="button"
						tabIndex={0}
						className="reminders-modal-bg"
						onClick={() => {
							closeSetReminder();
						}}
						onKeyUp={() => {
							closeSetReminder();
						}}
					/>
					<div className="reminders-modal-inner">
						<button
							className="modal-close-button reminders-close-button flex h-full"
							type="button"
							onClick={() => closeSetReminder()}
						>
							<CloseThin
								width={18}
								height={18}
								className="text-white"
							/>
						</button>
						<div className="reminders-modal-title">
							{!business.reminder?.hasReminderTaskSet
								? 'Set an email '
								: 'Email '}
							reminder for <span>{business.name}</span>
						</div>
						{!business.reminder?.hasReminderTaskSet ? (
							<div className="time-picker">
								<div className="time-picker-label">
									How long before you would like to be
									reminded?
								</div>
								<div className="time-picker-holder">
									<div className="time-picker-dropdown">
										<select
											id="month-picker"
											onChange={setHourZero}
										>
											{months.map(month => {
												return (
													<option
														key={month}
														value={month}
													>
														{month}{' '}
														{month === 1
															? 'month'
															: 'months'}
													</option>
												);
											})}
										</select>
									</div>
									<div className="time-picker-dropdown">
										<select
											id="day-picker"
											onChange={setHourZero}
										>
											{days.map(day => {
												return (
													<option
														key={day}
														value={day}
													>
														{day}{' '}
														{day === 1
															? 'day'
															: 'days'}
													</option>
												);
											})}
										</select>
									</div>
									<div className="time-picker-dropdown">
										<select id="hour-picker">
											{hours.map(hour => {
												return (
													<option
														key={hour}
														value={hour}
													>
														{hour}{' '}
														{hour === 1
															? 'hour'
															: 'hours'}
													</option>
												);
											})}
										</select>
									</div>
								</div>
							</div>
						) : (
							<div className="reminder-set-time">
								Your reminder email will send on{' '}
								{dayjs
									.unix(
										business.reminder.reminderTriggerDate,
										user.timeZone ? user.timeZone : ''
									)
									.format('MM/DD/YYYY')}{' '}
								at{' '}
								{dayjs
									.unix(
										business.reminder.reminderTriggerDate,
										user.timeZone ? user.timeZone : ''
									)
									.format('h:mm A')}{' '}
								{user.timeZone ? `${user.timeZone} time` : ''}
							</div>
						)}

						<div className="reminders-modal-notes">
							{business &&
							!business.reminder?.hasReminderTaskSet ? (
								<textarea
									className="text-note"
									placeholder="Add a note for your reminder here."
								/>
							) : (
								<textarea
									className="text-note"
									value={
										business &&
										business.reminder?.reminderText
											? business.reminder.reminderText
											: 'No text was provided for the reminder'
									}
									readOnly
									onFocus={evt => {
										evt.target.blur();
									}}
								/>
							)}
						</div>
						{!business.reminder?.hasReminderTaskSet ? (
							<div
								role="button"
								tabIndex={0}
								className="reminders-modal-submit"
								onClick={() => {
									setReminder();
								}}
								onKeyUp={() => {
									setReminder();
								}}
							>
								Set Your Reminder
							</div>
						) : (
							''
						)}
						{!business.reminder?.hasReminderTaskSet ? (
							<div className="reminder-loader">
								{taskLoading ? (
									<Loader status={Status.LOADING} />
								) : (
									''
								)}
								{taskFailure ? (
									<div className="failure-mark">X</div>
								) : (
									''
								)}
								{taskSuccess ? (
									<Checkmark className="reminder-checkmark" />
								) : (
									''
								)}
							</div>
						) : (
							''
						)}
						{business.reminder?.hasReminderTaskSet ? (
							<div
								role="button"
								tabIndex={0}
								className="reminder-deletion-button"
								onClick={deletReminder}
								onKeyUp={deletReminder}
							>
								Delete This Reminder
							</div>
						) : (
							''
						)}
					</div>
				</div>
			</div>

			<div className="add-data-modal">
				<div className="add-data-modal-holder-inner">
					<div
						className="add-data-modal-bg"
						onClick={closeAddDataModal}
						onKeyUp={closeAddDataModal}
						aria-label="Open set data."
						role="button"
						tabIndex={0}
					/>
					<div className="add-data-modal-inner">
						<button
							className="modal-close-button reminders-close-button flex h-full"
							type="button"
							onClick={closeAddDataModal}
						>
							<CloseThin
								width={18}
								height={18}
								className="text-white"
							/>
						</button>
						<div className="add-data-modal-title">
							Add Contact Data to <span>{business.name}</span>
						</div>
						<div className="add-data-modal-type-holder">
							<div className="add-data-modal-type-label">
								What type of data would you like to add?
							</div>
							<div className="type-error-message">
								Please select a type
							</div>
							<div
								className={`add-data-modal-type add-data-modal-type-first ${userGenDataType === 'web-link' ? 'type-selected' : ''}`}
								id="web-link"
								onClick={setAsDataType}
								onKeyUp={setAsDataType}
								aria-label="Set data."
								role="button"
								tabIndex={0}
							>
								Web Link
							</div>
							<div
								className={`add-data-modal-type ${userGenDataType === 'email' ? 'type-selected' : ''}`}
								id="email"
								onClick={setAsDataType}
								onKeyUp={setAsDataType}
								aria-label="Open set data."
								role="button"
								tabIndex={0}
							>
								Email
							</div>
							<div
								className={`add-data-modal-type ${userGenDataType === 'phone' ? 'type-selected' : ''}`}
								id="phone"
								onClick={setAsDataType}
								onKeyUp={setAsDataType}
								aria-label="Set data."
								role="button"
								tabIndex={0}
							>
								Phone
							</div>
						</div>
						<div className="error-message email-error-message">
							Email incorrectly formatted
						</div>
						<div className="error-message email-exists-error-message">
							This email already exists
						</div>
						<div className="error-message link-error-message">
							Please format website link correctly
						</div>
						<div className="error-message empty-input-error-message">
							The field below cannot be empty
						</div>
						<div className="error-message phone-exists-error-message">
							This phone number already exists
						</div>
						<div className="error-message link-exists-error-message">
							This link already exists
						</div>
						<div className="error-message linkName-exists-error-message">
							This link name is already being used
						</div>
						<div className="add-data-modal-input-holder add-data-modal-input-top">
							<input
								id="input-user-data"
								className="top-input"
								type="text"
								onKeyDown={e => handleInputKeyDown(e)}
								placeholder={(() => {
									const emailOrPhone =
										userGenDataType === 'email'
											? 'eg. manager@company.com'
											: 'eg. (555)-638-2751';
									return userGenDataType === 'web-link'
										? 'eg. Customer Website'
										: emailOrPhone;
								})()}
							/>
						</div>
						<div
							className={`add-data-modal-input-holder add-data-modal-input-bottom ${userGenDataType !== 'web-link' ? 'do-not-show' : ''}`}
						>
							<input
								className="bottom-input"
								id="user-input-url"
								type="text"
								placeholder="eg. www.examplewebsite.com"
							/>
						</div>
						<div
							id="user-add-data-submit"
							className="add-data-modal-submit"
							onClick={setUserGeneratedData}
							onKeyUp={setUserGeneratedData}
							aria-label="Set data."
							role="button"
							tabIndex={0}
						>
							{addBusinessDataLoading ? (
								<div className="add-data-loader">
									<Loader status={Status.LOADING} />
								</div>
							) : (
								''
							)}
							{addBusinessDataFailure ? (
								<div className="add-data-loader">
									<Loader status={Status.FAILURE} />
								</div>
							) : (
								''
							)}
							Add Data
						</div>
					</div>
				</div>
			</div>

			<div
				className="report-data-modal report-data-modal-closed report-data-modal-fade"
				id="report-data-modal"
			>
				<div
					className="report-data-modal-background"
					onClick={closeReportDataModal}
				/>
				<div className="report-data-modal-inner">
					<div className="report-data-modal-title">
						Report Incorrect Data
					</div>
					<div>Please indicate the data you wish to report.</div>
					<span
						className="report-data-modal-dropdown-header"
						ref={reportDataDropdownRef}
						onClick={() =>
							setReportDataDropdownOpened(
								!reportDataDropdownOpened
							)
						}
					>
						{reportDataSelected ||
							'Select an option from the dropdown'}
						<Chevron className="report-data-modal-dropdown-header-chevron" />
					</span>
					<div
						className={`report-data-modal-dropdown ${reportDataDropdownOpened ? 'opened' : ''}`}
					>
						{business?.email &&
							business?.emailLowPriority !== true && (
								<div
									className={`report-data-modal-dropdown-item ${reportDataSelected === business.email ? 'selected' : ''}`}
									onClick={() =>
										setReportDataSelected(business.email)
									}
								>
									{business.email}
								</div>
							)}
						{business?.facebook?.email &&
							business?.facebook?.emailLowPriority !== true &&
							business?.facebook?.email !== business?.email && (
								<div
									className={`report-data-modal-dropdown-item ${reportDataSelected === business.facebook.email ? 'selected' : ''}`}
									onClick={() =>
										setReportDataSelected(
											business.facebook.email
										)
									}
								>
									{business.facebook.email}
								</div>
							)}
						{business?.web?.email &&
							business?.web?.email !== business?.email &&
							business?.web?.emailLowPriority !== true &&
							business?.web?.email !==
								business?.facebook?.email && (
								<div
									className={`report-data-modal-dropdown-item ${reportDataSelected === business.web.email ? 'selected' : ''}`}
									onClick={() =>
										setReportDataSelected(
											business.web.email
										)
									}
								>
									{business.web.email}
								</div>
							)}
						{business?.facebook?.messengerUrl && (
							<div
								className={`report-data-modal-dropdown-item ${reportDataSelected === 'Facebook Messenger' ? 'selected' : ''}`}
								onClick={() =>
									setReportDataSelected('Facebook Messenger')
								}
							>
								Facebook Messenger
							</div>
						)}
						{business?.socials?.instagram && (
							<div
								className={`report-data-modal-dropdown-item ${reportDataSelected === 'Instagram Messenger' ? 'selected' : ''}`}
								onClick={() =>
									setReportDataSelected('Instagram Messenger')
								}
							>
								Instagram Messenger
							</div>
						)}
						{business?.phone?.phone && (
							<div
								className={`report-data-modal-dropdown-item ${reportDataSelected === business.phone.phone ? 'selected' : ''}`}
								onClick={() =>
									setReportDataSelected(business.phone.phone)
								}
							>
								{business.phone.phone}
							</div>
						)}
					</div>
					<div>
						Do you wish to share further details regarding this
						data?
					</div>
					<input
						className="report-data-modal-input"
						placeholder="Share any additional information or details here"
						value={reportDataModalInputText}
						onChange={e =>
							setReportDataModalInputText(e.target.value)
						}
					/>
					<div className="report-data-modal-button-holder">
						<button
							type="button"
							className="report-data-modal-button-cancel"
							onClick={closeReportDataModal}
						>
							Cancel
						</button>
						<button
							type="button"
							className={`report-data-modal-button-report ${!reportDataSelected ? 'disabled' : ''} ${reportBadDataLoading ? 'loading' : ''} `}
							onClick={() => {
								if (reportDataSelected) {
									sendCustomerReportBadData(
										business?._id || business?.businessId,
										reportDataSelected,
										user.email,
										reportDataModalInputText
									);

									sendAnalytics({
										type: 'track',
										userId: user.email,
										eventName:
											'User attempted bad data report',
										properties: {
											businessId:
												business?._id ||
												business?.businessId,
											incorrectData: reportDataSelected,
											additionalString:
												reportDataModalInputText
										}
									});
								}
							}}
						>
							{!reportBadDataLoading && 'Report'}
							{reportBadDataLoading && (
								<Loader
									className="report-data-modal-button-report-loading-icon"
									status={Status.LOADING}
								/>
							)}
							{reportBadDataFailure && (
								<>
									<ExclamationMarkRed className="report-data-modal-button-report-failure-icon" />
									<span className="report-data-modal-button-failure-text">
										Reporting failed. Please try again.
									</span>
								</>
							)}
						</button>
					</div>
				</div>
			</div>

			<div
				className="delete-modal delete-modal-closed delete-modal-fade"
				id="delete-modal"
			>
				<div
					className="delete-modal-background"
					onClick={() => closeModal('delete-modal')}
				/>
				<div className="delete-inner-modal">
					<div className="delete-modal-title">
						Are you sure you want to discard this email draft?
						&nbsp;
						<p>This email draft will be permanently deleted.</p>
					</div>
					<div className="delete-button-holder">
						<button
							className="cancel-delete"
							onClick={() => closeModal('delete-modal')}
						>
							Cancel
						</button>
						<button
							className="delete-permanently"
							onClick={onClickDelete}
						>
							<p>Delete permanently</p>
						</button>
					</div>
				</div>
			</div>

			<div
				className="email-unavailable-modal email-unavailable-modal-closed email-unavailable-modal-fade"
				id="email-unavailable-modal"
			>
				<div
					className="email-unavailable-modal-background"
					onClick={closeEmailUnavailableModal}
				/>
				<div className="email-unavailable-inner-modal">
					<div className="email-unavailable-modal-title">
						Compose new email not available on mobile &nbsp;
						<p>
							To compose a new email to a list or contact please
							navigate to our site on a desktop browser.
						</p>
					</div>
					<div
						className="email-unavailable-button-holder"
						onClick={closeEmailUnavailableModal}
					>
						Close
					</div>
				</div>
			</div>

			<div
				className="delete-data-modal delete-data-modal-closed delete-data-modal-fade"
				id="delete-data-modal"
			>
				<div
					className="delete-data-modal-background"
					onClick={() => (
						setDeleteDataIndex(null),
						closeModal('delete-data-modal')
					)}
				/>
				<div className="delete-data-inner-modal">
					<div className="delete-data-modal-title">
						Are you sure you want to delete this contact data?
						&nbsp;
						<p>This action cannot be undone.</p>
					</div>
					<span className="delete-data-button-holder">
						<button
							className="delete-data-secondary"
							onClick={() => (
								setDeleteDataIndex(null),
								closeModal('delete-data-modal')
							)}
						>
							Cancel
						</button>
						<button
							id="contact-data-delete"
							className="delete-data-primary"
							onClick={() => {
								deleteUserAddedData(
									deleteDataIndex,
									business._id ||
										business.businessId ||
										business.id,
									user.email
								);
								setDeleteDataIndex(null);
								closeModal('delete-data-modal');
							}}
						>
							<p>Delete permanently</p>
						</button>
					</span>
				</div>
			</div>
		</>
	);
};

export default BusinessPreview;
